import { LocationStrategy, PathLocationStrategy, registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import localeEs from '@angular/common/locales/es';
import {
	APP_INITIALIZER,
	type ApplicationConfig,
	importProvidersFrom,
	isDevMode,
	LOCALE_ID,
	provideZoneChangeDetection,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
// Guards are now functions, no need to import classes
import { AuthInterceptor } from '@interceptors/http-interceptor';
import { provideTransloco } from '@jsverse/transloco';
import Aura from '@primeuix/themes/aura';
import { AuthService } from '@services/auth/auth.service';
import { AuthApiService } from '@services/auth/auth-api.service';
import { ConfigService } from '@services/configuration/config.service';
import { LanguageInitService } from '@services/domain/language-init.service';

import primelocaleEs from 'primelocale/es.json';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { TranslocoHttpLoader } from './transloco-loader';

registerLocaleData(localeEs);

export const appConfig: ApplicationConfig = {
	providers: [
		provideZoneChangeDetection({ eventCoalescing: true }),
		provideRouter(routes, withComponentInputBinding()),
		importProvidersFrom(BrowserModule, FormsModule, ReactiveFormsModule),
		provideHttpClient(withFetch(), withInterceptors([AuthInterceptor])),
		provideAnimations(),

		providePrimeNG({
			theme: {
				preset: Aura,
				options: {
					prefix: 'p',
					darkModeSelector: 'none',
					cssLayer: false,
				},
			},
			ripple: true,
			translation: primelocaleEs.es,
		}),

		{ provide: LocationStrategy, useClass: PathLocationStrategy },
		{ provide: LOCALE_ID, useValue: 'es-ES' },
		MessageService,
		AuthService,
		ConfirmationService,
		DynamicDialogRef,
		DynamicDialogConfig,
		DialogService,
		AuthApiService,
		LanguageInitService,
		{
			provide: APP_INITIALIZER,
			useFactory: (configService: ConfigService) => () => configService.loadConfig().toPromise(),
			deps: [ConfigService],
			multi: true,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: (languageInitService: LanguageInitService) => () => languageInitService.initializeLanguage(),
			deps: [LanguageInitService],
			multi: true,
		},
		provideTransloco({
			config: {
				availableLangs: ['es'],
				defaultLang: 'es',
				fallbackLang: 'es',
				reRenderOnLangChange: true,
				prodMode: !isDevMode(),
				missingHandler: {
					logMissingKey: false,
					useFallbackTranslation: true,
					allowEmpty: false,
				},
			},
			loader: TranslocoHttpLoader,
		}),

		// Service Worker for PWA - Production only
		provideServiceWorker('ngsw-worker.js', {
			enabled: environment.production,
			registrationStrategy: 'registerWhenStable:30000',
		}),
	],
};
